import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Loader2, Plus, Trash2, Save, Languages, CheckCircle2 } from 'lucide-react'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { useAbout } from '@/services/use-about'

const localizedStringSchema = z.object({
  tr: z.string().min(1, 'Türkçe alan zorunludur'),
  en: z.string().optional(),
  fr: z.string().optional(),
  es: z.string().optional(),
  ru: z.string().optional(),
  nl: z.string().optional(),
  ar: z.string().optional(),
})

const aboutSchema = z.object({
  hero: z.object({
    title: localizedStringSchema,
    description: localizedStringSchema,
  }),
  stats: z.array(z.object({
    value: z.string().min(1, 'Değer zorunludur'),
    label: localizedStringSchema,
  })),
  mainContent: z.object({
    title: localizedStringSchema,
    subtitle: localizedStringSchema,
    description: localizedStringSchema,
    extraDescription: localizedStringSchema,
  }),
  values: z.array(z.object({
    title: localizedStringSchema,
    description: localizedStringSchema,
  })),
  milestones: z.array(z.object({
    year: z.string().min(1, 'Yıl zorunludur'),
    title: localizedStringSchema,
    description: localizedStringSchema,
  })),
})

type AboutFormValues = z.infer<typeof aboutSchema>

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
  { title: 'Hakkımızda CMS', href: '/hakkimizda', isActive: true },
]

const LANGUAGES = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'ar', label: 'العربية' },
]

function HakkimizdaPage() {
  const { about, isLoading, updateAbout, isUpdating } = useAbout()
  const [isNormalSaving, setIsNormalSaving] = useState(false)
  const [isAISaving, setIsAISaving] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('')

  const form = useForm<AboutFormValues>({
    resolver: zodResolver(aboutSchema),
    defaultValues: {
      hero: { title: { tr: '' }, description: { tr: '' } },
      stats: [],
      mainContent: { title: { tr: '' }, subtitle: { tr: '' }, description: { tr: '' }, extraDescription: { tr: '' } },
      values: [],
      milestones: [],
    },
  })

  // ... (useFieldArray hooks)

  const { fields: statFields, append: appendStat, remove: removeStat } = useFieldArray({
    control: form.control,
    name: 'stats',
  })

  const { fields: valueFields, append: appendValue, remove: removeValue } = useFieldArray({
    control: form.control,
    name: 'values',
  })

  const { fields: milestoneFields, append: appendMilestone, remove: removeMilestone } = useFieldArray({
    control: form.control,
    name: 'milestones',
  })

  useEffect(() => {
    if (about) {
      form.reset({
        hero: about.hero,
        stats: about.stats,
        mainContent: about.mainContent,
        values: about.values,
        milestones: about.milestones,
      })
    }
  }, [about, form])

  const onSubmit = async (values: AboutFormValues) => {
    setIsNormalSaving(true)
    try {
      await updateAbout({ data: values })
    } finally {
      setIsNormalSaving(false)
    }
  }

  const onTranslateAndSave = async () => {
    const values = form.getValues()
    setIsAISaving(true)
    setShowProgress(true)
    setProgress(5)
    setCurrentStep('AI Çeviri servisine bağlanılıyor...')

    const steps = [
      { p: 15, m: 'Giriş (Hero) bölümü çeviriliyor...' },
      { p: 30, m: 'İstatistikler çeviriliyor...' },
      { p: 50, m: 'Ana içerik metinleri çeviriliyor...' },
      { p: 70, m: 'Değerlerimiz ve vizyonumuz çeviriliyor...' },
      { p: 85, m: 'Kilometre taşları ve tarihçe çeviriliyor...' },
      { p: 98, m: 'Veriler işleniyor, bu biraz zaman alabilir...' },
    ]

    let currentStepIdx = 0
    const interval = setInterval(() => {
      // Sadece 98%'e kadar otomatik ilerle
      if (currentStepIdx < steps.length) {
        setProgress(steps[currentStepIdx].p)
        setCurrentStep(steps[currentStepIdx].m)
        currentStepIdx++
      }
    }, 1200)

    try {
      await updateAbout({ data: values, translate: true })
      clearInterval(interval)
      setProgress(100)
      setCurrentStep('Tüm diller başarıyla kaydedildi!')
      setTimeout(() => setShowProgress(false), 2000)
    } catch (error) {
      clearInterval(interval)
      setShowProgress(false)
    } finally {
      setIsAISaving(false)
    }
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[80vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Hakkımızda Sayfası Yönetimi</h1>
                <p className="text-muted-foreground text-sm">Site üzerindeki hakkımızda içeriğini ve istatistikleri düzenleyin.</p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onTranslateAndSave}
                  disabled={isUpdating}
                  className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                >
                  {isAISaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  AI ile Çevir ve Kaydet
                </Button>
                <Button type="submit" disabled={isUpdating}>
                  {isNormalSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Normal Kaydet
                </Button>
              </div>
            </div>

            <Tabs defaultValue="hero" className="w-full">
              <TabsList className="flex flex-wrap h-auto w-full">
                <TabsTrigger value="hero" className="flex-1">Giriş (Hero)</TabsTrigger>
                <TabsTrigger value="stats" className="flex-1">İstatistikler</TabsTrigger>
                <TabsTrigger value="main" className="flex-1">Ana İçerik</TabsTrigger>
                <TabsTrigger value="values" className="flex-1">Değerlerimiz</TabsTrigger>
                <TabsTrigger value="history" className="flex-1">Kilometre Taşları</TabsTrigger>
              </TabsList>

              {/* HERO SECTION */}
              <TabsContent value="hero" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Giriş Bölümü</CardTitle>
                    <CardDescription>Sayfanın en üstünde görünen başlık ve kısa açıklama.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Tabs defaultValue="tr">
                      <TabsList>
                        {LANGUAGES.map(lang => (
                          <TabsTrigger key={lang.code} value={lang.code}>{lang.label}</TabsTrigger>
                        ))}
                      </TabsList>
                      {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                          <FormField
                            control={form.control}
                            name={`hero.title.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Başlık ({lang.label})</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Örn: Üretim Gücü" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`hero.description.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Açıklama ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} placeholder="Giriş açıklaması..." rows={3} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* STATS SECTION */}
              <TabsContent value="stats" className="space-y-4 pt-4">
                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={() => appendStat({ value: '', label: { tr: '' } })}>
                    <Plus className="mr-2 h-4 w-4" /> İstatistik Ekle
                  </Button>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {statFields.map((field, index) => (
                    <Card key={field.id} className="flex flex-col">
                      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                        <CardTitle className="text-sm font-medium">İstatistik #{index + 1}</CardTitle>
                        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeStat(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-4 flex-1">
                        <FormField
                          control={form.control}
                          name={`stats.${index}.value`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Değer</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Örn: 25+ / 10.000m²" className="h-8 text-sm" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Tabs defaultValue="tr" className="w-full">
                          <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                            {LANGUAGES.map(lang => (
                              <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.label}</TabsTrigger>
                            ))}
                          </TabsList>
                          {LANGUAGES.map(lang => (
                            <TabsContent key={lang.code} value={lang.code} className="pt-2">
                              <FormField
                                control={form.control}
                                name={`stats.${index}.label.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Etiket ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Örn: Yıllık Tecrübe" className="h-8 text-sm" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* MAIN CONTENT SECTION */}
              <TabsContent value="main" className="space-y-4 pt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Ana Bilgi Bölümü</CardTitle>
                    <CardDescription>Şirket mirası ve detaylı tanıtım yazıları.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Tabs defaultValue="tr">
                      <TabsList>
                        {LANGUAGES.map(lang => (
                          <TabsTrigger key={lang.code} value={lang.code}>{lang.label}</TabsTrigger>
                        ))}
                      </TabsList>
                      {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                          <FormField
                            control={form.control}
                            name={`mainContent.title.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Ana Başlık ({lang.label})</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Örn: Yarım Asırlık Üretim Mirası" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`mainContent.subtitle.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Alt Başlık ({lang.label})</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Örn: SKG Metal hakkında..." />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`mainContent.description.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Açıklama Paragraf 1 ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} rows={4} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`mainContent.extraDescription.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Açıklama Paragraf 2 ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} rows={4} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* VALUES SECTION */}
              <TabsContent value="values" className="space-y-4 pt-4">
                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={() => appendValue({ title: { tr: '' }, description: { tr: '' } })}>
                    <Plus className="mr-2 h-4 w-4" /> Değer Ekle
                  </Button>
                </div>
                <div className="space-y-4">
                  {valueFields.map((field, index) => (
                    <Card key={field.id}>
                      <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Değer #{index + 1}</CardTitle>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeValue(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <Tabs defaultValue="tr">
                          <TabsList>
                            {LANGUAGES.map(lang => (
                              <TabsTrigger key={lang.code} value={lang.code}>{lang.label}</TabsTrigger>
                            ))}
                          </TabsList>
                          {LANGUAGES.map(lang => (
                            <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                              <FormField
                                control={form.control}
                                name={`values.${index}.title.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Başlık ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Örn: Kalite" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`values.${index}.description.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Açıklama ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Textarea {...field} rows={2} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* HISTORY / MILESTONES SECTION */}
              <TabsContent value="history" className="space-y-4 pt-4">
                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={() => appendMilestone({ year: '', title: { tr: '' }, description: { tr: '' } })}>
                    <Plus className="mr-2 h-4 w-4" /> Kilometre Taşı Ekle
                  </Button>
                </div>
                <div className="space-y-4">
                  {milestoneFields.map((field, index) => (
                    <Card key={field.id}>
                      <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-sm font-medium">Kilometre Taşı #{index + 1}</CardTitle>
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeMilestone(index)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <FormField
                          control={form.control}
                          name={`milestones.${index}.year`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Yıl</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="Örn: 1975" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <Tabs defaultValue="tr">
                          <TabsList>
                            {LANGUAGES.map(lang => (
                              <TabsTrigger key={lang.code} value={lang.code}>{lang.label}</TabsTrigger>
                            ))}
                          </TabsList>
                          {LANGUAGES.map(lang => (
                            <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                              <FormField
                                control={form.control}
                                name={`milestones.${index}.title.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Başlık ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Input {...field} placeholder="Örn: Kuruluş" />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name={`milestones.${index}.description.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>Açıklama ({lang.label})</FormLabel>
                                    <FormControl>
                                      <Textarea {...field} rows={2} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </form>
        </Form>
      </Main>
      <Dialog open={showProgress} onOpenChange={setShowProgress}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Languages className="h-5 w-5 text-blue-600" />
              Yapay Zeka Çevirisi
            </DialogTitle>
            <DialogDescription>
              İçeriğiniz 7 farklı dile profesyonelce çevriliyor. Lütfen bekleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm font-medium">
                <span>{currentStep}</span>
                <span>%{progress}</span>
              </div>
              <Progress value={progress} className="h-2 w-full" />
            </div>
            {progress === 100 && (
              <div className="flex items-center justify-center gap-2 text-green-600 animate-in fade-in zoom-in duration-300">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium text-sm">Başarıyla tamamlandı ve kaydedildi!</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export const Route = createFileRoute('/_authenticated/hakkimizda/')({
  component: HakkimizdaPage,
})
